import React, { useEffect, useState, useRef } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import './PlayerTournamentPage.css';
import { doc, updateDoc, getDoc, setDoc, getDocs } from 'firebase/firestore';
import { arrayUnion } from 'firebase/firestore';
import { addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { playSound } from '../utils/soundPlayer';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// import DeckScreenshotUpload from '../components/DeckScreenshotUpload';

const PlayerTournamentPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [user, setUser] = useState(null);
    const [matches, setMatches] = useState([]);
    // NOTE SYSTEM - COMMENTED OUT (REPLACED BY CHAT SYSTEM)
    // const [editedNotes, setEditedNotes] = useState({});
    const [editedResults, setEditedResults] = useState({});
    const [userMatches, setUserMatches] = useState([]);
    const [masterMatches, setMasterMatches] = useState({});
    const [tournamentNames, setTournamentNames] = useState({});
    const [tournamentStatuses, setTournamentStatuses] = useState({});
    const [submittingResult, setSubmittingResult] = useState({});
    // const [savingNote, setSavingNote] = useState({});
    const [confirming, setConfirming] = useState({});
    const [chatMessages, setChatMessages] = useState({});
    const [chatInputs, setChatInputs] = useState({});
    const [consecutiveMessages, setConsecutiveMessages] = useState({}); // Track consecutive messages per match
    const [cooldowns, setCooldowns] = useState({}); // Track cooldowns per match
    const [unreadMessages, setUnreadMessages] = useState({}); // Track unread messages per match
    const [messageStatuses, setMessageStatuses] = useState({}); // Track message status (sent, delivered, read)
    const [lastReadTimestamps, setLastReadTimestamps] = useState({}); // Track when user last read each match
    // const [tournamentScreenshots, setTournamentScreenshots] = useState({});
    const [userTournaments, setUserTournaments] = useState([]);
    const chatRefs = useRef({});
    const tournamentRefs = useRef({});

    const formatTimestamp = (timestamp) => {
        if (!timestamp?.seconds) return '';
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Handle scrolling to specific tournament from notification (only once)
    useEffect(() => {
        const tournamentId = searchParams.get('tournamentId');
        if (tournamentId && matches.length > 0) {
            // Check if we've already scrolled to this tournament
            const hasScrolled = sessionStorage.getItem(`scrolledToTournament_${tournamentId}`);
            if (!hasScrolled) {
                // Wait a bit for the DOM to render, then scroll to the tournament
                setTimeout(() => {
                    const tournamentElement = tournamentRefs.current[tournamentId];
                    if (tournamentElement) {
                        tournamentElement.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                        });
                        // Highlight the tournament briefly
                        tournamentElement.style.backgroundColor = '#ffeb3b';
                        setTimeout(() => {
                            tournamentElement.style.backgroundColor = '';
                        }, 2000);
                        // Mark that we've scrolled to this tournament
                        sessionStorage.setItem(`scrolledToTournament_${tournamentId}`, 'true');
                    }
                }, 500);
            }
        }
    }, [searchParams, matches]);

    // const handleScreenshotUploaded = (tournamentId, screenshotUrl, uploadedAt) => {
    //     setTournamentScreenshots(prev => ({
    //         ...prev,
    //         [tournamentId]: {
    //             url: screenshotUrl,
    //             uploadedAt: uploadedAt
    //         }
    //     }));
    // };

    // Anti-spam system functions
    const canSendMessage = (matchId) => {
        const match = matches.find(m => m.id === matchId);
        if (!match) return false;

        const currentUserId = user.uid;
        const opponentId = currentUserId === match.playerAId ? match.playerBId : match.playerAId;
        
        // Check if user is in cooldown
        const cooldown = cooldowns[matchId];
        if (cooldown && cooldown.userId === currentUserId) {
            const now = Date.now();
            if (now < cooldown.until) {
                return false; // Still in cooldown
            }
        }

        // Get recent messages for this match
        const recentMessages = (chatMessages[matchId] || [])
            .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
            .slice(0, 10); // Check last 10 messages

        // Count consecutive messages from current user
        let consecutiveCount = 0;
        for (const msg of recentMessages) {
            if (msg.senderId === currentUserId) {
                consecutiveCount++;
            } else {
                break; // Stop counting when we hit opponent's message
            }
        }

        // Allow if less than 5 consecutive messages
        return consecutiveCount < 5;
    };

    const getCooldownTimeRemaining = (matchId) => {
        const cooldown = cooldowns[matchId];
        if (!cooldown || cooldown.userId !== user.uid) return 0;
        
        const now = Date.now();
        return Math.max(0, cooldown.until - now);
    };

    const formatCooldownTime = (milliseconds) => {
        const minutes = Math.ceil(milliseconds / (1000 * 60));
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}m`;
        }
        return `${minutes}m`;
    };

    const handleSendMessage = async (matchId) => {
        const text = (chatInputs[matchId] || '').trim();
        if (!text) return;

        // Check if user can send message
        if (!canSendMessage(matchId)) {
            const cooldownTime = getCooldownTimeRemaining(matchId);
            if (cooldownTime > 0) {
                toast.error(`You're in cooldown for ${formatCooldownTime(cooldownTime)}. Wait for your opponent to respond or wait for the cooldown to expire.`);
            } else {
                toast.error('You can only send 5 consecutive messages. Wait for your opponent to respond.');
            }
            return;
        }

        const match = matches.find(m => m.id === matchId);
        if (!match) return;

        try {
            const chatRef = collection(db, 'tournament-v2', 'upcoming', 'list', match.tournamentId, 'TournamentSchedule', match.id, 'chat');
            const messageData = {
                text,
                senderId: user.uid,
                senderName: user.pseudo || 'Anonymous',
                timestamp: serverTimestamp(),
                status: 'sent',
                readBy: {},
                deliveredAt: null,
                readAt: null
            };
            
            const messageRef = await addDoc(chatRef, messageData);
            
            // Update message status to delivered immediately after sending
            setTimeout(async () => {
                try {
                    await updateDoc(messageRef, {
                        status: 'delivered',
                        deliveredAt: serverTimestamp()
                    });
                } catch (error) {
                    console.error('Error updating message status to delivered:', error);
                }
            }, 1000);

            // Clear input
            setChatInputs(prev => ({ ...prev, [matchId]: '' }));

            // Check if this is the 5th consecutive message
            const recentMessages = (chatMessages[matchId] || [])
                .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
                .slice(0, 10);

            let consecutiveCount = 0;
            for (const msg of recentMessages) {
                if (msg.senderId === user.uid) {
                    consecutiveCount++;
                } else {
                    break;
                }
            }

            // If this would be the 5th consecutive message, set cooldown
            if (consecutiveCount >= 4) { // 4 previous + 1 current = 5
                const cooldownUntil = Date.now() + (60 * 60 * 1000); // 1 hour from now
                setCooldowns(prev => ({
                    ...prev,
                    [matchId]: {
                        userId: user.uid,
                        until: cooldownUntil
                    }
                }));
                toast.warning('You have reached the 5-message limit. You are now in cooldown for 1 hour or until your opponent responds.');
            }

        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message. Please try again.');
        }
    };

    const sendAdminNotification = async (text, matchId) => {
        try {
            await addDoc(collection(db, 'adminNotifications'), {
                text,
                matchId,
                playerId: user.uid,
                playerName: user.displayName ?? "Unknown",
                timestamp: serverTimestamp()
            });
        } catch (error) {
            console.error('❌ Failed to send admin notification:', error);
        }
    };

    // Mark messages as read for a specific match
    const markMessagesAsRead = async (matchId) => {
        if (!user?.uid) return;
        
        const match = matches.find(m => m.id === matchId);
        if (!match) return;

        try {
            const currentTime = serverTimestamp();
            
            // Update last read timestamp for this match
            setLastReadTimestamps(prev => ({
                ...prev,
                [matchId]: currentTime
            }));

            // Get unread messages from opponent
            const opponentId = user.uid === match.playerAId ? match.playerBId : match.playerAId;
            const unreadOpponentMessages = (chatMessages[matchId] || [])
                .filter(msg => 
                    msg.senderId === opponentId && 
                    (!msg.readBy || !msg.readBy[user.uid])
                );

            // Mark each unread message as read
            for (const message of unreadOpponentMessages) {
                try {
                    const messageRef = doc(db, 'tournament-v2', 'upcoming', 'list', match.tournamentId, 'TournamentSchedule', match.id, 'chat', message.id);
                    await updateDoc(messageRef, {
                        [`readBy.${user.uid}`]: currentTime,
                        status: 'read'
                    });
                } catch (error) {
                    console.error('Error marking message as read:', error);
                }
            }

            // Clear unread count for this match
            setUnreadMessages(prev => ({
                ...prev,
                [matchId]: 0
            }));

        } catch (error) {
            console.error('Error marking messages as read:', error);
        }
    };

    // Calculate unread message count for a match
    const getUnreadCount = (matchId) => {
        if (!user?.uid) return 0;
        
        const match = matches.find(m => m.id === matchId);
        if (!match) return 0;

        const opponentId = user.uid === match.playerAId ? match.playerBId : match.playerAId;
        
        // Count only messages from opponent that haven't been marked as read
        return (chatMessages[matchId] || [])
            .filter(msg => 
                msg.senderId === opponentId && 
                (!msg.readBy || !msg.readBy[user.uid])
            ).length;
    };

    // Get total unread count across all matches
    const getTotalUnreadCount = () => {
        return Object.keys(chatMessages).reduce((total, matchId) => {
            return total + getUnreadCount(matchId);
        }, 0);
    };

    // Helper to propagate updates to master and both user match docs
    const propagateMatchUpdate = async (match, updateObj) => {
        // 1️⃣ Always update master
        const masterRef = doc(
            db, 'tournament-v2', 'upcoming', 'list',
            match.tournamentId, 'TournamentSchedule', match.id
        );
        await updateDoc(masterRef, updateObj);

        // 2️⃣ Get full updated master after update
        const masterSnap = await getDoc(masterRef);
        if (!masterSnap.exists()) throw new Error('Master match not found!');
        const masterData = masterSnap.data();

        // 3️⃣ Determine what was updated
        const isNoteUpdate = ('playerANote' in updateObj || 'playerBNote' in updateObj);

        const userARef = doc(db, 'users', match.playerAId, 'matches', match.id);
        const userBRef = doc(db, 'users', match.playerBId, 'matches', match.id);

        if (isNoteUpdate) {
            // ✅ This is a note update → only save the player's own note to their doc
            const userField = (user.uid === match.playerAId) ? 'playerANote' : 'playerBNote';
            const userMatchRef = doc(db, 'users', user.uid, 'matches', match.id);
            await setDoc(userMatchRef, { [userField]: updateObj[userField] }, { merge: true });
        } else {
            // ✅ General update → fan-out full master data to BOTH users
            const fanOut = {
                matchId: match.id,
                tournamentId: match.tournamentId,
                playerAId: match.playerAId,
                playerAName: match.playerAName,
                playerBId: match.playerBId,
                playerBName: match.playerBName,
                playerANote: masterData.playerANote ?? '',
                playerBNote: masterData.playerBNote ?? '',
                confirmedBy: masterData.confirmedBy ?? [],
                status: masterData.status ?? '',
                reports: masterData.reports ?? {},
                createdAt: match.createdAt ?? serverTimestamp()
            };

            await Promise.all([
                setDoc(userARef, fanOut, { merge: true }),
                setDoc(userBRef, fanOut, { merge: true })
            ]);
        }
    };

    const handleConfirmAppointment = async (matchId) => {
        setConfirming(prev => ({ ...prev, [matchId]: true }));
        try {
            const match = matches.find(m => m.id === matchId);
            if (!match) return toast.error(t('playerPage.matchNotFound'));
            await propagateMatchUpdate(match, {
                confirmedBy: arrayUnion(user.uid)
            });
            await sendAdminNotification(
                `confirmed appointment in match ${matchId}`,
                matchId
            );
            toast.success(t('playerPage.appointmentConfirmed'));
        } catch (error) {
            toast.error(t('playerPage.failedSubmitResult'));
        } finally {
            setConfirming(prev => ({ ...prev, [matchId]: false }));
        }
    };

    const handleSubmitResult = async (matchId) => {
        setSubmittingResult(prev => ({ ...prev, [matchId]: true }));
        const selectedResult = editedResults[matchId];
        if (!selectedResult) {
            toast.error(t('playerPage.pleaseSelectResult'));
            setSubmittingResult(prev => ({ ...prev, [matchId]: false }));
            return;
        }
        try {
            const match = matches.find(m => m.id === matchId);
            if (!match) return toast.error(t('playerPage.matchNotFound'));
            
            // Update master first
            const masterRef = doc(
                db, 'tournament-v2', 'upcoming', 'list',
                match.tournamentId, 'TournamentSchedule', match.id
            );
            await updateDoc(masterRef, {
                [`reports.${user.uid}`]: selectedResult
            });
            
            // Try to send admin notification, but don't fail if it errors
            sendAdminNotification(
                `reported result (${selectedResult}) in match ${matchId}`,
                matchId
            ).catch(err => {
                console.error('Failed to send admin notification:', err);
            });
            
            // Try to propagate to user docs, but don't fail if it errors
            try {
                const masterSnap = await getDoc(masterRef);
                if (masterSnap.exists()) {
                    const masterData = masterSnap.data();
                    const userARef = doc(db, 'users', match.playerAId, 'matches', match.id);
                    const userBRef = doc(db, 'users', match.playerBId, 'matches', match.id);
                    
                    const fanOut = {
                        matchId: match.id,
                        tournamentId: match.tournamentId,
                        playerAId: match.playerAId,
                        playerAName: match.playerAName,
                        playerBId: match.playerBId,
                        playerBName: match.playerBName,
                        playerANote: masterData.playerANote ?? '',
                        playerBNote: masterData.playerBNote ?? '',
                        confirmedBy: masterData.confirmedBy ?? [],
                        status: masterData.status ?? '',
                        reports: masterData.reports ?? {},
                        createdAt: match.createdAt ?? serverTimestamp()
                    };
                    
                    await Promise.all([
                        setDoc(userARef, fanOut, { merge: true }),
                        setDoc(userBRef, fanOut, { merge: true })
                    ]);
                }
            } catch (fanOutError) {
                console.error('Failed to propagate to user docs:', fanOutError);
                // Don't fail the submission if fan-out fails
            }
            
            toast.success(t('playerPage.resultSubmitted'));
        } catch (error) {
            console.error('Error submitting result:', error);
            toast.error(t('playerPage.failedSubmitResult'));
        } finally {
            setSubmittingResult(prev => ({ ...prev, [matchId]: false }));
        }
    };

    // NOTE SYSTEM - COMMENTED OUT (REPLACED BY CHAT SYSTEM)
    /*
    const handleSavePlayerNote = async (matchId) => {
        setSavingNote(prev => ({ ...prev, [matchId]: true }));
        const newNote = editedNotes[matchId]?.trim();
        if (!newNote) {
            toast.error(t('playerPage.pleaseEnterSuggestion'));
            setSavingNote(prev => ({ ...prev, [matchId]: false }));
            return;
        }
        const match = matches.find(m => m.id === matchId);
        if (!match) {
            toast.error(t('playerPage.matchNotFound'));
            setSavingNote(prev => ({ ...prev, [matchId]: false }));
            return;
        }
        const fieldName = user.uid === match.playerAId ? 'playerANote' : 'playerBNote';
        try {
            await propagateMatchUpdate(match, {
                [fieldName]: newNote
            });
            await sendAdminNotification(
                `updated their appointment suggestion in match ${matchId}`,
                matchId
            );
            toast.success(t('playerPage.suggestionSaved'));
        } catch (error) {
            toast.error(t('playerPage.failedSaveSuggestion'));
        } finally {
            setSavingNote(prev => ({ ...prev, [matchId]: false }));
        }
    };
    */

    useEffect(() => {
        if (!user) return;

        const q = collection(db, 'users', user.uid, 'matches');
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({
                id: doc.id,
                ref: doc.ref,
                ...doc.data(),
            }));
            setUserMatches(list);
        });

        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        if (!userMatches.length) return;

        const unsubscribers = [];

        userMatches.forEach((match) => {
            const masterRef = doc(
                db, 'tournament-v2', 'upcoming', 'list', match.tournamentId, 'TournamentSchedule', match.id
            );

            // 👇 Listen to match master data
            const unsubMatch = onSnapshot(masterRef, async (snap) => {
                if (snap.exists()) {
                    setMasterMatches((prev) => ({
                        ...prev,
                        [match.id]: {
                            id: snap.id,
                            ...snap.data()
                        }
                    }));

                    // 👇 Fetch tournament name if missing
                    const tournamentId = match.tournamentId;
                    if (!tournamentNames[tournamentId]) {
                        try {
                            const tourDoc = await getDoc(doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId));
                            if (tourDoc.exists()) {
                                setTournamentNames((prev) => ({
                                    ...prev,
                                    [tournamentId]: tourDoc.data().title ?? tournamentId
                                }));
                            }
                        } catch (err) {
                            console.error(`❌ Failed to fetch tournament name for ${tournamentId}`, err);
                        }
                    }
                }
            });
            unsubscribers.push(unsubMatch);

            // 👇 Listen to chat messages
            const chatRef = collection(
                db, 'tournament-v2', 'upcoming', 'list', match.tournamentId,
                'TournamentSchedule', match.id, 'chat'
            );
            const unsubChat = onSnapshot(chatRef, (snapshot) => {
                const messages = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => a.timestamp?.seconds - b.timestamp?.seconds);

                // Check for new messages from opponent
                const previousMessages = chatMessages[match.id] || [];
                const newMessages = messages.filter(newMsg => 
                    !previousMessages.some(prevMsg => prevMsg.id === newMsg.id) &&
                    newMsg.senderId !== user.uid
                );

                // Play sound for new messages from opponent
                if (newMessages.length > 0) {
                    playSound('message');
                }

                setChatMessages(prev => ({ ...prev, [match.id]: messages }));
            });
            unsubscribers.push(unsubChat);
        });

        return () => unsubscribers.forEach(unsub => unsub());
    }, [userMatches]);

    useEffect(() => {
        if (!user) return;

        console.log('PlayerTournamentPage loading for UID:', user.uid);

        // 🔎 ONE listener over ALL tournaments’ participants where docId == user.uid
        // Fetch tournaments where user is a participant
        const fetchUserTournaments = async () => {
            try {
                // Get all upcoming tournaments
                const tournamentsRef = collection(db, 'tournament-v2', 'upcoming', 'list');
                const tournamentsSnap = await getDocs(tournamentsRef);
                
                const participantTournamentIds = [];
                
                for (const tournamentDoc of tournamentsSnap.docs) {
                    const tournamentId = tournamentDoc.id;
                    
                    // Check if user is a participant in this tournament
                    const participantRef = doc(db, 'tournament-v2', 'upcoming', 'list', tournamentId, 'participants', user.uid);
                    const participantSnap = await getDoc(participantRef);
                    
                    if (participantSnap.exists()) {
                        participantTournamentIds.push(tournamentId);
                        
                                                 // Cache tournament title and status if missing
                         if (!tournamentNames[tournamentId] || !tournamentStatuses[tournamentId]) {
                             const tourData = tournamentDoc.data();
                             setTournamentNames((prev) => ({
                                 ...prev,
                                 [tournamentId]: tourData.title ?? tournamentId
                             }));
                             setTournamentStatuses((prev) => ({
                                 ...prev,
                                 [tournamentId]: tourData.started
                             }));
                         }
                    }
                }
                
                setUserTournaments(participantTournamentIds);
            } catch (error) {
                console.error('Error fetching user tournaments:', error);
            }
        };

        fetchUserTournaments();



        // Keep your per-user matches listener (used elsewhere in the page)
        const myMatchesRef = collection(db, 'users', user.uid, 'matches');
        const unsubscribeMatches = onSnapshot(myMatchesRef, (snapshot) => {
            const list = snapshot.docs.map((doc) => ({
                id: doc.id,
                ref: doc.ref,
                ...doc.data(),
            }));
            setMatches(list);
        });

        return () => {
            unsubscribeMatches();
        };
    }, [user, tournamentNames]);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);

                // 🔍 Fetch pseudo from Firestore
                const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
                if (userDoc.exists()) {
                    const pseudo = userDoc.data().pseudo || 'Unknown';
                    setUser((prev) => ({
                        ...prev,
                        pseudo
                    }));
                }
            }
        });

        return () => unsubscribe();
    }, []);

    // Auto-scroll chat messages container to show newest messages
    useEffect(() => {
        Object.keys(chatRefs.current).forEach(matchId => {
            const el = chatRefs.current[matchId];
            if (el) {
                // Find the chat-messages container (parent of the ref element)
                const chatMessagesContainer = el.closest('.chat-messages');
                if (chatMessagesContainer) {
                    // Scroll the chat container to bottom, not the entire page
                    chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;
                }
            }
        });
    }, [chatMessages]);

    // Monitor chat messages to reset cooldowns when opponent responds
    useEffect(() => {
        if (!user?.uid) return;

        Object.keys(chatMessages).forEach(matchId => {
            const match = matches.find(m => m.id === matchId);
            if (!match) return;

            const currentUserId = user.uid;
            const opponentId = currentUserId === match.playerAId ? match.playerBId : match.playerAId;
            
            // Get the most recent message
            const recentMessages = (chatMessages[matchId] || [])
                .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
            
            if (recentMessages.length > 0) {
                const lastMessage = recentMessages[0];
                
                // If the last message is from the opponent, clear any cooldown for current user
                if (lastMessage.senderId === opponentId) {
                    setCooldowns(prev => {
                        const newCooldowns = { ...prev };
                        if (newCooldowns[matchId]?.userId === currentUserId) {
                            delete newCooldowns[matchId];
                        }
                        return newCooldowns;
                    });
                }
            }
        });
    }, [chatMessages, matches, user?.uid]);

    // Update unread counts when messages change
    useEffect(() => {
        if (!user?.uid) return;

        const newUnreadCounts = {};
        Object.keys(chatMessages).forEach(matchId => {
            newUnreadCounts[matchId] = getUnreadCount(matchId);
        });
        
        setUnreadMessages(newUnreadCounts);
    }, [chatMessages, lastReadTimestamps, user?.uid]);

    // Auto-mark messages as read when chat is visible and user is active
    useEffect(() => {
        if (!user?.uid) return;

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // User is back on the page, mark all visible chats as read
                Object.keys(chatMessages).forEach(matchId => {
                    const chatElement = document.querySelector(`[data-match-id="${matchId}"] .chat-messages`);
                    if (chatElement && isElementInViewport(chatElement)) {
                        markMessagesAsRead(matchId);
                    }
                });
            }
        };

        const handleScroll = () => {
            // Mark messages as read when scrolling through chats
            Object.keys(chatMessages).forEach(matchId => {
                const chatElement = document.querySelector(`[data-match-id="${matchId}"] .chat-messages`);
                if (chatElement && isElementInViewport(chatElement)) {
                    markMessagesAsRead(matchId);
                }
            });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [chatMessages, user?.uid]);

    // Helper function to check if element is in viewport
    const isElementInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    };

    return (
        <div className="player-tournament-container">
            <h2>{t('playerPage.title')}</h2>


            {/* Show deck screenshot uploads for all tournaments the user is participating in */}
            <div className="tournament-overview">
                {/* Show tournaments with matches */}
                {matches.length > 0 && (
                    <>
                        {Object.entries(
                            matches.reduce((acc, match) => {
                                if (!acc[match.tournamentId]) {
                                    acc[match.tournamentId] = [];
                                }
                                acc[match.tournamentId].push(match);
                                return acc;
                            }, {})
                        ).map(([tournamentId, tournamentMatches]) => (
                            <div key={tournamentId} className="" ref={(el) => tournamentRefs.current[tournamentId] = el}>
                             {/*<h3 className="tournament-title"> {tournamentNames[tournamentId] ?? tournamentId}</h3>*/}

                                {/* Deck Screenshot Upload for this tournament - COMMENTED OUT (replaced by new deck submission system) */}
                                {/* <DeckScreenshotUpload
                                    tournamentId={tournamentId}
                                    userId={user.uid}
                                    tournamentName={tournamentNames[tournamentId] ?? tournamentId}
                                    tournamentStatus={tournamentStatuses[tournamentId] ?? 'upcoming'}
                                    onScreenshotUploaded={(screenshotUrl, uploadedAt) =>
                                        handleScreenshotUploaded(tournamentId, screenshotUrl, uploadedAt)
                                    }
                                /> */}

                                {/* Matches for this tournament */}
                                <div className="tournament-matches">
                                    {tournamentMatches.map((match) => (
                                        <div key={match.id} className="match-card">
                                            <h2 className='playertournament-title'>{tournamentNames[match.tournamentId] ?? match.tournamentId}</h2>
                                            <h3>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H5V21H19V9Z" fill="currentColor"/>
                                                </svg>
                                                {t('playerPage.round')}: {match.round ?? t('playerPage.unassigned')}
                                            </h3>
                                            <p><strong>{t('playerPage.opponent')}:</strong> <span className="opponent-name">{user.uid === match.playerAId ? match.playerBName : match.playerAName}</span></p>
                                            <p><strong>{t('playerPage.status')}:</strong> {match.status}</p>

                                            {/* NOTE SYSTEM - COMMENTED OUT (REPLACED BY CHAT SYSTEM) */}
                                            {/* 
                                            <div className="section">
                                                <div className="section-title">{t('playerPage.adminNote')}</div>
                                                <p>{match.adminNote?.trim() || t('playerPage.noAdminNote')}</p>
                                            </div>

                                            <div className="section">
                                                <div className="section-title">{t('playerPage.opponentSuggestion')}</div>
                                                <p>
                                                    {user.uid === match.playerAId
                                                        ? (masterMatches[match.id]?.playerBNote?.trim() || t('playerPage.noSuggestion'))
                                                        : (masterMatches[match.id]?.playerANote?.trim() || t('playerPage.noSuggestion'))}
                                                </p>
                                            </div>

                                            <div className="section">
                                                <div className="section-title">{t('playerPage.yourSuggestion')}</div>
                                                <textarea
                                                    value={editedNotes[match.id] ?? (user.uid === match.playerAId ? match.playerANote : match.playerBNote) ?? ''}
                                                    onChange={(e) =>
                                                        setEditedNotes((prev) => ({
                                                            ...prev,
                                                            [match.id]: e.target.value
                                                        }))
                                                    }
                                                    placeholder={t('playerPage.suggestionPlaceholder')}
                                                />
                                                <button onClick={() => handleSavePlayerNote(match.id)} disabled={!!savingNote[match.id]}>{t('playerPage.saveSuggestion')}</button>
                                            </div>
                                            */}

                                            <div className="report-section">
                                                <div className="section-title">{t('playerPage.reportTitle')}</div>
                                                <p>{t('playerPage.yourReport')}: {match.reports?.[user.uid] ?? 'Not submitted yet.'}</p>
                                                <p>{t('playerPage.opponentReport')}: {Object.entries(match.reports || {}).filter(([uid]) => uid !== user.uid).map(([uid, res]) => res).join(", ") || t('playerPage.notSubmitted')}</p>

                                                <select
                                                    value={editedResults[match.id] ?? ''}
                                                    onChange={(e) =>
                                                        setEditedResults((prev) => ({
                                                            ...prev,
                                                            [match.id]: e.target.value
                                                        }))
                                                    }
                                                >
                                                    <option value="">{t('playerPage.selectResult')}</option>
                                                    <option value="Win 2-0">{t('playerPage.win2_0')}</option>
                                                    <option value="Win 2-1">{t('playerPage.win2_1')}</option>
                                                    <option value="Win 1-0">{t('playerPage.win1_0')}</option>
                                                    <option value="Loss 0-2">{t('playerPage.loss0_2')}</option>
                                                    <option value="Loss 1-2">{t('playerPage.loss1_2')}</option>
                                                    <option value="Loss 0-1">{t('playerPage.loss0_1')}</option>
                                                    <option value="DRAW">{t('playerPage.draw')}</option>
                                                </select>
                                                <button onClick={() => handleSubmitResult(match.id)} disabled={!!submittingResult[match.id]}>{t('playerPage.submitResult')}</button>
                                            </div>

                                            <div className="chat-section" data-match-id={match.id}>
                                                <div className="section-title">
                                                    <div className="chat-title-row">
                                                        <span className="chat-title">
                                                            {t('playerPage.chatWithOpponent')}
                                                            {unreadMessages[match.id] > 0 && (
                                                                <span className="unread-badge">
                                                                    {unreadMessages[match.id]}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {(() => {
                                                        const cooldownTime = getCooldownTimeRemaining(match.id);
                                                        const canSend = canSendMessage(match.id);
                                                        
                                                        if (cooldownTime > 0) {
                                                            return (
                                                                <span className="cooldown-indicator">
                                                                    🔒 Cooldown: {formatCooldownTime(cooldownTime)}
                                                                </span>
                                                            );
                                                        } else if (!canSend) {
                                                            return (
                                                                <span className="spam-warning">
                                                                    ⚠️ 5 messages sent - wait for opponent
                                                                </span>
                                                            );
                                                        } else {
                                                            // Show how many consecutive messages user has sent
                                                            const recentMessages = (chatMessages[match.id] || [])
                                                                .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
                                                                .slice(0, 10);
                                                            
                                                            let consecutiveCount = 0;
                                                            for (const msg of recentMessages) {
                                                                if (msg.senderId === user.uid) {
                                                                    consecutiveCount++;
                                                                } else {
                                                                    break;
                                                                }
                                                            }
                                                            
                                                            if (consecutiveCount > 0) {
                                                                return (
                                                                    <span className="message-count">
                                                                        {consecutiveCount}/5 messages
                                                                    </span>
                                                                );
                                                            }
                                                        }
                                                        return null;
                                                    })()}
                                                </div>
                                                
                                                <div className="chat-messages" onScroll={() => markMessagesAsRead(match.id)}>
                                                    {[...(chatMessages[match.id] || [])]
                                                        .sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0))
                                                        .map(msg => (
                                                                <div
                                                                    key={msg.id}
                                                                    className={`chat-message ${msg.isAdminMessage ? 'admin' : (msg.senderId === user.uid ? 'own' : 'opponent')} ${msg.senderId === user.uid && msg.status ? `status-${msg.status}` : ''}`}
                                                                >
                                                                    <div className="chat-header">
                                                                        <strong className={msg.isAdminMessage ? 'admin-name' : ''}>
                                                                            {msg.isAdminMessage ? 'Admin' : (msg.senderName || t('playerPage.unknown'))}
                                                                        </strong>
                                                                    <div className="chat-meta">
                                                                        <span className="chat-time">{formatTimestamp(msg.timestamp)}</span>
                                                                        {msg.senderId === user.uid && (
                                                                            <span className="message-status">
                                                                                {msg.status === 'sent' && (
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                        <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                                                                                    </svg>
                                                                                )}
                                                                                {msg.status === 'delivered' && (
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                        <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                                                                                    </svg>
                                                                                )}
                                                                                {msg.status === 'read' && (
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                        <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                                                                                        <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor" opacity="0.6" transform="translate(-2, 0)"/>
                                                                                    </svg>
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="chat-text">{msg.text}</div>
                                                            </div>
                                                        ))}
                                                    <div ref={el => chatRefs.current[match.id] = el} />
                                                </div>

                                                <input
                                                    type="text"
                                                    value={chatInputs[match.id] || ''}
                                                    onChange={(e) => setChatInputs(prev => ({ ...prev, [match.id]: e.target.value }))}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleSendMessage(match.id);
                                                        }
                                                    }}
                                                    placeholder={t('playerPage.typeMessage')}
                                                    className="chat-input"
                                                    disabled={!canSendMessage(match.id)}
                                                />
                                                <button
                                                    onClick={() => handleSendMessage(match.id)}
                                                    disabled={!canSendMessage(match.id)}
                                                >
                                                    {t('playerPage.send')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                )}

                {/* Show tournaments without matches (for deck screenshot uploads) - COMMENTED OUT (replaced by new deck submission system) */}
                {/* 
                {userTournaments.length > 0 && (
                    <>
                        {userTournaments.map((tournamentId) => {
                            // Skip tournaments that already have matches (they're shown above)
                            if (matches.some(match => match.tournamentId === tournamentId)) {
                                return null;
                            }

                            return (
                                <div key={tournamentId} className="tournament-section">
                                    <h3 className="tournament-title"> {tournamentNames[tournamentId] ?? tournamentId}</h3>

                                    <div className="tournament-info">
                                        <p>📋 Tournament registration confirmed. Waiting for pairings to be announced.</p>
                                        <p>Please submit your deck in the Deck Builder page to complete your tournament preparation.</p>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
                */}

                {/* Show message if no tournaments at all */}
                {matches.length === 0 && userTournaments.length === 0 && (
                    <div className="no-tournaments">
                        <p>{t('playerPage.noMatches')}</p>
                        <p>{t('playerPage.notRegisteredForTournaments')}</p>
                    </div>
                )}
            </div>

            <button
                className="back-button"
                onClick={() => {
                    playSound('return');
                    navigate('/tournament');
                }}
            >
                {t('tournament.back')}
            </button>
        </div>
    );
};

export default PlayerTournamentPage;